import { FormattedMessage } from 'react-intl';
import { Send, ThumbsDown, ThumbsUp } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function RatingButtons({
  selectedRating,
  onRatingSelect,
}: {
  selectedRating: 'positive' | 'negative' | null;
  onRatingSelect: (rating: 'positive' | 'negative') => void;
}): JSX.Element {
  return (
    <div className="flex gap-2">
      <Button
        variant={selectedRating === 'positive' ? 'default' : 'outline'}
        size="sm"
        onClick={() => onRatingSelect('positive')}
        className="flex items-center gap-2"
      >
        <ThumbsUp className="w-4 h-4" />
        <FormattedMessage id="rca.feedback.helpful" />
      </Button>
      <Button
        variant={selectedRating === 'negative' ? 'default' : 'outline'}
        size="sm"
        onClick={() => onRatingSelect('negative')}
        className="flex items-center gap-2"
      >
        <ThumbsDown className="w-4 h-4" />
        <FormattedMessage id="rca.feedback.notHelpful" />
      </Button>
    </div>
  );
}

export function CommentForm({
  selectedRating,
  comment,
  onCommentChange,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  selectedRating: 'positive' | 'negative';
  comment: string;
  onCommentChange: (comment: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {selectedRating === 'positive' ? (
          <ThumbsUp className="w-4 h-4 text-green-600" />
        ) : (
          <ThumbsDown className="w-4 h-4 text-red-600" />
        )}
        <span className="text-sm font-medium">
          <FormattedMessage
            id={
              selectedRating === 'positive'
                ? 'rca.feedback.positiveSelected'
                : 'rca.feedback.negativeSelected'
            }
          />
        </span>
      </div>
      <div className="space-y-2">
        <Textarea
          placeholder="Share your feedback about this investigation (optional)"
          value={comment}
          onChange={e => onCommentChange(e.target.value)}
          className="min-h-[80px]"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <FormattedMessage id="rca.feedback.sharedWithTeam" />
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          onClick={onSubmit}
          disabled={isSubmitting}
          size="sm"
          className="flex items-center gap-2"
        >
          <Send className="w-4 h-4" />
          <FormattedMessage id="rca.feedback.submit" />
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          size="sm"
        >
          <FormattedMessage id="common.cancel" />
        </Button>
      </div>
    </div>
  );
}
